import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
  type FirestoreError,
} from 'firebase/firestore'

import type { GoalMode, Score } from '@study-decks/shared'

import { getDb } from './firebase.js'

export class ScoreApiError extends Error {
  override readonly name = 'ScoreApiError'

  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

export interface NewScoreInput {
  deckId: string
  classId: string | null
  goalId: string | null
  mode: GoalMode
  startedAt: string
  endedAt: string
  durationSeconds: number
  cardsSeen: number
  cardsCorrect: number
  accuracy: number
  missedCardIds: string[]
  clientTimezone: string
}

type SnapshotLike = { id: string; data(): Record<string, unknown> }

function stringValue(value: unknown) {
  return typeof value === 'string' ? value : ''
}

function scoreFromSnapshot(snapshot: SnapshotLike): Score {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    userId: stringValue(data.userId),
    deckId: stringValue(data.deckId),
    classId: typeof data.classId === 'string' ? data.classId : null,
    goalId: typeof data.goalId === 'string' ? data.goalId : null,
    mode: data.mode === 'speedMatch' ? 'speedMatch' : 'flashcards',
    startedAt: stringValue(data.startedAt),
    endedAt: stringValue(data.endedAt),
    durationSeconds: typeof data.durationSeconds === 'number' ? data.durationSeconds : 0,
    cardsSeen: typeof data.cardsSeen === 'number' ? data.cardsSeen : 0,
    cardsCorrect: typeof data.cardsCorrect === 'number' ? data.cardsCorrect : 0,
    accuracy: typeof data.accuracy === 'number' ? data.accuracy : 0,
    missedCardIds: Array.isArray(data.missedCardIds)
      ? data.missedCardIds.filter((id): id is string => typeof id === 'string')
      : [],
    clientTimezone: stringValue(data.clientTimezone),
  }
}

function errorCode(err: unknown): string {
  const code = (err as Partial<FirestoreError>).code
  if (code === 'permission-denied') return 'PERMISSION_DENIED'
  if (code === 'invalid-argument' || code === 'failed-precondition') return 'INVALID_ARGUMENT'
  if (code === 'unavailable') return 'UNAVAILABLE'
  return 'REQUEST_FAILED'
}

function errorMessage(code: string): string {
  if (code === 'INVALID_ARGUMENT') return "Couldn't save your session — please try again."
  if (code === 'PERMISSION_DENIED') return "You don't have access to record this score."
  if (code === 'UNAVAILABLE') {
    return "Couldn't reach the database — your session is saved locally; retry."
  }
  return 'Something went wrong — please try again.'
}

function normalizeError(err: unknown): ScoreApiError {
  if (err instanceof ScoreApiError) return err
  const code = errorCode(err)
  return new ScoreApiError(code, errorMessage(code))
}

export async function createScore(input: NewScoreInput, userUid: string): Promise<Score> {
  const ref = doc(collection(getDb(), 'scores'))
  const score: Score = {
    id: ref.id,
    userId: userUid,
    ...input,
  }

  try {
    await setDoc(ref, score)
    return score
  } catch (err) {
    throw normalizeError(err)
  }
}

export async function listMyRecentScores(uid: string, limit = 10): Promise<Score[]> {
  try {
    const scoresQuery = query(collection(getDb(), 'scores'), where('userId', '==', uid))
    const snapshot = await getDocs(scoresQuery)
    return snapshot.docs
      .map(scoreFromSnapshot)
      .sort((a, b) => b.endedAt.localeCompare(a.endedAt))
      .slice(0, limit)
  } catch (err) {
    throw normalizeError(err)
  }
}

export async function getPersonalBest(
  uid: string,
  deckId: string,
  mode: GoalMode,
): Promise<Score | null> {
  const scores = await listMyRecentScores(uid, 100)
  return (
    scores
      .filter((score) => score.deckId === deckId && score.mode === mode)
      .sort((a, b) => {
        if (b.accuracy !== a.accuracy) return b.accuracy - a.accuracy
        return a.durationSeconds - b.durationSeconds
      })[0] ?? null
  )
}
